import React, { useState, useContext } from "react";

import classNames from "classnames";
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
} from "recharts";

import ThemeContext from "contexts/ThemeContext";

import { Typography, PageLoader } from "./clearsense-ui/components";
import ContentBox from "./ContentBox";

const COLORS = ["#8500ff", "#2360de"];

const GRAPH_STROKE_COLORS = { dark: "#FFFFFF", light: "#333333" };

const noop = () => {};

const SummaryBarChart = ({
  title = "",
  data = [],
  isLoading = false,
  topFiveAction = noop,
  bottomFiveAction = noop,
  isValuePercent = false,
  className,
}) => {
  const { theme } = useContext(ThemeContext);
  const [isTopSelected, setIsTopSelected] = useState(true);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="border border-black dark:border-gray-200 border-solid bg-white dark:bg-gray-700 opacity-95 dark:opacity-100 p-2">
          {payload?.map(
            ({ color, name, value: dataValue }, index) =>
              name !== "name" && (
                <p style={{ color }} key={index}>{`${name} : ${dataValue}${
                  isValuePercent ? "%" : ""
                }`}</p>
              )
          )}
        </div>
      );
    }

    return null;
  };

  const renderLegend = props => {
    const { payload, iconSize } = props;

    return (
      <div className="flex gap-4 justify-center w-full">
        {payload?.map(
          ({ value, color }, index) =>
            value !== "name" && (
              <div
                key={`item-${index}`}
                className="flex gap-1 items-center dark:text-white"
              >
                <div
                  style={{
                    backgroundColor: color,
                    width: iconSize,
                    height: iconSize,
                  }}
                />
                {value}
              </div>
            )
        )}
      </div>
    );
  };

  return (
    <ContentBox
      className={classNames(
        "flex flex-col justify-center items-center bg-white dark:bg-[#2f3941] p-0 pr-4 pb-3",
        className
      )}
    >
      <div className="flex justify-center w-full">
        <Typography
          style="h4"
          type="semibold"
          className="dark:text-white flex justify-center w-full"
        >
          {title}
        </Typography>
      </div>
      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="w-full h-[32vh] flex flex-nowrap">
          <div className="m-8 mr-0 border-2 h-full w-full border-solid border-black dark:border-white pt-4 my-2 pr-8 min-w-0">
            {data?.[0] ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} barSize={30} barGap={0}>
                  <XAxis dataKey="name" stroke={GRAPH_STROKE_COLORS?.[theme]} />
                  <YAxis
                    stroke={GRAPH_STROKE_COLORS?.[theme]}
                    tickFormatter={value =>
                      isValuePercent ? `${value}%` : value
                    }
                  />
                  <Tooltip content={CustomTooltip} />
                  <Legend content={renderLegend} />
                  {Object.keys(data?.[0])?.map((keyName, index) => (
                    <Bar
                      dataKey={keyName}
                      key={index}
                      fill={COLORS[index % 2]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                style="h4"
                type="semibold"
                className="w-full h-full flex justify-center items-center"
              >
                No Data
              </Typography>
            )}
          </div>
          <div>
            <Typography
              style="h5"
              type="semibold"
              className={classNames(
                "w-20 p-2 mt-2 border-l-0 whitespace-nowrap border-2 border-solid border-black transition hover:bg-blue-100 cursor-pointer",
                { "bg-gray-400": isTopSelected }
              )}
              onClick={() => {
                topFiveAction();
                setIsTopSelected(true);
              }}
            >
              Top 5
            </Typography>
            <Typography
              style="h5"
              type="semibold"
              className={classNames(
                "w-20 p-2 border-l-0 whitespace-nowrap border-2 border-solid border-black transition hover:bg-blue-100 border-t-0 cursor-pointer",
                { "bg-gray-400": !isTopSelected }
              )}
              onClick={() => {
                bottomFiveAction();
                setIsTopSelected(false);
              }}
            >
              Bottom 5
            </Typography>
          </div>
        </div>
      )}
    </ContentBox>
  );
};

export default SummaryBarChart;
